import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { isManager, canEdit } from "../lib/roles";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useSettings, logoUrl } from "../context/SettingsContext";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import StudentFormDialog from "../components/StudentFormDialog";
import GenderIcon from "../components/GenderIcon";
import { ArrowLeft, ArrowRight, Printer, Check, X, Plane, Trash2, Pencil, Save, Phone, GraduationCap, CalendarCheck, CalendarX, Percent, CalendarDays, Loader2 } from "lucide-react";

function StatBox({ icon: Icon, label, value, tint }) {
  return (
    <div className="surface-card rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: tint + "22", color: tint }}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 text-3xl font-semibold text-foreground">{value}</div>
    </div>
  );
}

export default function StudentDetail() {
  const { id } = useParams();
  const { t, dir } = useLang();
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const isSuper = isManager(user);
  const editable = canEdit(user);
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/students/${id}/history`);
      setData(r.data);
      setNotes(r.data.student.notes || "");
    } catch { toast.error("Error"); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/groups").then((r) => setGroups(r.data)).catch(() => {}); }, []);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/students/${id}/notes`, { notes });
      toast.success(t("detail.notesSaved"));
    } catch { toast.error("Error"); } finally { setSavingNotes(false); }
  };

  const deleteRecord = async (recordId) => {
    if (!window.confirm(t("detail.confirmDelete"))) return;
    try {
      await api.delete(`/attendance/${recordId}`);
      toast.success(t("detail.deleted"));
      load();
    } catch { toast.error("Error"); }
  };

  const deleteStudent = async () => {
    if (!window.confirm(t("detail.confirmDeleteStudent"))) return;
    try {
      await api.delete(`/students/${id}`);
      toast.success(t("detail.studentDeleted"));
      navigate("/");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!data) return <div className="text-muted-foreground">{t("common.loading")}</div>;

  const { student, summary, records, present_dates, absent_dates, leave_dates } = data;
  const Back = dir === "rtl" ? ArrowRight : ArrowLeft;
  const logo = logoUrl(settings.logo_path);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 no-print">
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground">
          <Back className="h-4 w-4" /> {t("detail.back")}
        </button>
        <div className="flex items-center gap-2">
          {isSuper && (
            <button onClick={() => setShowEdit(true)} data-testid="edit-student-btn" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary">
              <Pencil className="h-4 w-4" /> {t("detail.editStudent")}
            </button>
          )}
          {editable && (
            <button onClick={deleteStudent} data-testid="delete-student-btn" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10">
              <Trash2 className="h-4 w-4" /> {t("detail.deleteStudent")}
            </button>
          )}
          <button onClick={() => window.print()} data-testid="print-btn" className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90">
            <Printer className="h-4 w-4" /> {t("detail.print")}
          </button>
        </div>
      </div>

      <div className="no-print space-y-6">
        <div className="surface-card rounded-xl border border-border bg-card p-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl text-white" style={{ backgroundColor: "var(--brand)" }}>
              <GraduationCap className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-2xl font-semibold text-foreground">{student.name}</h1>
                <GenderIcon gender={student.gender} className="h-5 w-5" />
              </div>
              <p className="text-sm text-muted-foreground">
                {t("detail.id")}: {student.number} · {t("detail.group")}: {student.group}
              </p>
            </div>
            {(student.phone1 || student.phone2) && (
              <div className="space-y-1 text-sm">
                {student.phone1 && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" /> <span dir="ltr">{student.phone1}</span></div>}
                {student.phone2 && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" /> <span dir="ltr">{student.phone2}</span></div>}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatBox icon={CalendarCheck} label={t("detail.present")} value={summary.present} tint="#10B981" />
          <StatBox icon={CalendarX} label={t("detail.absent")} value={summary.absent} tint="#EF4444" />
          <StatBox icon={Plane} label={t("attendance.leave")} value={summary.leave} tint="#F59E0B" />
          <StatBox icon={CalendarDays} label={t("detail.total")} value={summary.total} tint="#4F46E5" />
          <StatBox icon={Percent} label={t("detail.percentage")} value={`${summary.percentage}%`} tint="#6366F1" />
        </div>

        <div className="surface-card rounded-xl border border-border bg-card p-5">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">{t("detail.percentage")}</span>
            <span className="font-semibold brand-text">{summary.percentage}%</span>
          </div>
          <Progress value={summary.percentage} className="h-2.5" />
        </div>

        {/* notes */}
        <div className="surface-card rounded-xl border border-border bg-card p-5">
          <h3 className="mb-3 font-medium text-foreground">{t("detail.notes")}</h3>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("detail.notesPlaceholder")} data-testid="student-notes" rows={3} readOnly={!editable} />
          <div className={`mt-3 flex justify-end ${editable ? "" : "hidden"}`}>
            <button onClick={saveNotes} disabled={savingNotes} data-testid="save-notes-btn" className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">
              {savingNotes ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {t("detail.saveNotes")}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="surface-card rounded-xl border border-border bg-card p-5">
            <h3 className="mb-3 flex items-center gap-2 font-medium text-foreground"><CalendarCheck className="h-4 w-4 text-emerald-600" /> {t("detail.presentDates")}</h3>
            <div className="flex flex-wrap gap-2">
              {present_dates.length === 0 && <span className="text-sm text-muted-foreground">—</span>}
              {present_dates.map((d) => <span key={d} className="rounded-md bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700" dir="ltr">{d}</span>)}
            </div>
          </div>
          <div className="surface-card rounded-xl border border-border bg-card p-5">
            <h3 className="mb-3 flex items-center gap-2 font-medium text-foreground"><CalendarX className="h-4 w-4 text-red-600" /> {t("detail.absentDates")}</h3>
            <div className="flex flex-wrap gap-2">
              {absent_dates.length === 0 && <span className="text-sm text-muted-foreground">—</span>}
              {absent_dates.map((d) => <span key={d} className="rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-700" dir="ltr">{d}</span>)}
            </div>
          </div>
          <div className="surface-card rounded-xl border border-border bg-card p-5">
            <h3 className="mb-3 flex items-center gap-2 font-medium text-foreground"><Plane className="h-4 w-4 text-amber-600" /> {t("detail.leaveDates")}</h3>
            <div className="flex flex-wrap gap-2">
              {(leave_dates || []).length === 0 && <span className="text-sm text-muted-foreground">—</span>}
              {(leave_dates || []).map((d) => <span key={d} className="rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700" dir="ltr">{d}</span>)}
            </div>
          </div>
        </div>

        <div className="surface-card overflow-hidden rounded-xl border border-border bg-card">
          <div className="border-b border-border px-5 py-4">
            <h3 className="font-medium text-foreground">{t("detail.history")}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/60">
                  <th className="px-5 py-3 text-start font-medium text-muted-foreground">{t("detail.date")}</th>
                  <th className="px-5 py-3 text-start font-medium text-muted-foreground">{t("detail.status")}</th>
                  {isSuper && <th className="px-5 py-3 text-center font-medium text-muted-foreground">{t("detail.delete")}</th>}
                </tr>
              </thead>
              <tbody>
                {records.length === 0 && <tr><td colSpan={3} className="px-5 py-8 text-center text-muted-foreground">{t("detail.noRecords")}</td></tr>}
                {records.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0" data-testid={`history-row-${r.id}`}>
                    <td className="px-5 py-3 font-mono text-xs text-foreground" dir="ltr">{r.date}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${r.status === "present" ? "bg-emerald-100 text-emerald-700" : r.status === "leave" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {r.status === "present" ? <Check className="h-3.5 w-3.5" /> : r.status === "leave" ? <Plane className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        {r.status === "present" ? t("attendance.present") : r.status === "leave" ? t("attendance.leave") : t("attendance.absent")}
                      </span>
                    </td>
                    {isSuper && (
                      <td className="px-5 py-3 text-center">
                        <button onClick={() => deleteRecord(r.id)} data-testid={`delete-record-${r.id}`} aria-label={t("detail.delete")}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-destructive transition-colors hover:bg-destructive/10">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* print report */}
      <div id="print-report">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "2px solid #111", paddingBottom: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {logo ? <img src={logo} alt="logo" style={{ height: 56, width: 56, objectFit: "cover", borderRadius: 8 }} /> : null}
            <div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{settings.school_name}</div>
              <div style={{ fontSize: 13, color: "#444" }}>{t("detail.reportTitle")}</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#444", textAlign: dir === "rtl" ? "left" : "right" }}>
            <div>{t("detail.generated")}: {new Date().toISOString().slice(0, 10)}</div>
          </div>
        </div>

        <table style={{ width: "100%", marginBottom: 18, fontSize: 14 }}>
          <tbody>
            <tr>
              <td style={{ padding: "4px 0", fontWeight: 600, width: "50%" }}>{t("attendance.student")}: {student.name}</td>
              <td style={{ padding: "4px 0", fontWeight: 600 }}>{t("detail.id")}: {student.number}</td>
            </tr>
            <tr>
              <td style={{ padding: "4px 0", fontWeight: 600 }}>{t("detail.group")}: {student.group}</td>
              <td style={{ padding: "4px 0", fontWeight: 600 }}>{t("detail.percentage")}: {summary.percentage}%</td>
            </tr>
            {(student.phone1 || student.phone2) && (
              <tr>
                <td style={{ padding: "4px 0", fontWeight: 600 }} colSpan={2}>{t("detail.phone1")}: {student.phone1} {student.phone2 ? ` / ${student.phone2}` : ""}</td>
              </tr>
            )}
          </tbody>
        </table>

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20, fontSize: 13, textAlign: "center" }}>
          <tbody>
            <tr>
              <td style={{ border: "1px solid #999", padding: 8 }}><strong>{summary.present}</strong><br />{t("detail.present")}</td>
              <td style={{ border: "1px solid #999", padding: 8 }}><strong>{summary.absent}</strong><br />{t("detail.absent")}</td>
              <td style={{ border: "1px solid #999", padding: 8 }}><strong>{summary.leave}</strong><br />{t("attendance.leave")}</td>
              <td style={{ border: "1px solid #999", padding: 8 }}><strong>{summary.total}</strong><br />{t("detail.total")}</td>
              <td style={{ border: "1px solid #999", padding: 8 }}><strong>{summary.percentage}%</strong><br />{t("detail.percentage")}</td>
            </tr>
          </tbody>
        </table>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #999", padding: 8, textAlign: dir === "rtl" ? "right" : "left", background: "#f0f0f0" }}>{t("detail.date")}</th>
              <th style={{ border: "1px solid #999", padding: 8, textAlign: dir === "rtl" ? "right" : "left", background: "#f0f0f0" }}>{t("detail.status")}</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id}>
                <td style={{ border: "1px solid #999", padding: 8 }}>{r.date}</td>
                <td style={{ border: "1px solid #999", padding: 8 }}>{r.status === "present" ? t("attendance.present") : r.status === "leave" ? t("attendance.leave") : t("attendance.absent")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StudentFormDialog open={showEdit} onOpenChange={setShowEdit} groups={groups} student={student} onSaved={() => load()} />
    </div>
  );
}
